import React, { useState, useEffect } from "react";
import { Modal, Button, Form, FloatingLabel } from "react-bootstrap";
import styles from "./stylesAdmin.module.css"
import { SectorGde, ReparticionGde  } from "../../types/interfaces";
import { useLoading } from "../../components/LoadingContext";

interface AddSectorGdeModalProps {
  show: boolean;
  onHide: () => void;
  showNotification: (message: string, variant: "success" | "danger") => void;
  onAddItem?: (item: any) => void;
}

export default function AddSectorGdeModal({
  show,
  onHide,
  showNotification,
  onAddItem,
}: AddSectorGdeModalProps) {
  const { setLoading } = useLoading();
  const [formData, setFormData] = useState<any>({});
  const [reparticionesGde, setReparticionesGde] = useState<ReparticionGde[]>([]);
  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [reparticionesGdeResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/api/admin/allReparticionesGde`, { method: "GET", headers: {"Content-Type": "application/json",}, credentials: "include" }),
        ]);

        if (!reparticionesGdeResponse.ok) {
          throw new Error("Network response was not ok");
        }

        const [reparticionesGdeData] = await Promise.all([
          reparticionesGdeResponse.json(),
        ]);

        setReparticionesGde(reparticionesGdeData);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [setLoading]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData: any) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const newErrors: any = {};
    let hasErrors = false;

    if (!formData.nombre) {
        newErrors.nombre = "Este campo es obligatorio.";
        hasErrors = true;
    }
    if (!formData.reparticion_gde || !formData.reparticion_gde.nombre) {
      newErrors.reparticion_id = "Este campo es obligatorio";
      hasErrors = true;
    }

    setErrors(newErrors);
    return !hasErrors;
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;

    const findIdByName = (name: string, list: any[]) => {
      const selectedItem = list.find((item) => item.nombre === name);
      return selectedItem ? selectedItem.id : null;
    };

    setFormData((prevData: any) => {
      switch (name) {
        case "reparticion_gde.nombre":
          const reparticionId = findIdByName(value, reparticionesGde);
          return {
            ...prevData,
            reparticion_gde: { nombre: value, id: reparticionId },
          };
        default:
          return prevData;
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    setLoading(true);
    e.preventDefault();

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    const transformedData = {
        nombre: formData.nombre,
        reparticion_id: formData.reparticion_gde?.id || null
    };
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/createSectorGde`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(transformedData),
        credentials: "include"
      });

      if (!response.ok) {
        throw new Error('Failed to add sector gde');
      }

      const addedSectorGde = await response.json();
  
      const sectorGdeDetailsResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/sectorGde/${addedSectorGde.id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      const sectorGdeDetails = await sectorGdeDetailsResponse.json();

      showNotification("Sector GDE añadido con éxito", "success");
      setFormData({});
      onHide();
      if (onAddItem) onAddItem(sectorGdeDetails);
    } catch (error) {
      showNotification("Error al añadir sector GDE", "danger");
      console.error('Error adding sector:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} className={styles.modalContainer}>
              <Form onSubmit={handleSubmit}>
      <Modal.Header closeButton className={styles.modalHeader}>
        <Modal.Title className={styles.modalTitle}>Agregar Sector GDE</Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles.modalBody}>
      <FloatingLabel controlId="floatingSelectReparticionGde" label="Pertenece a" className="mb-3">
            <Form.Select aria-label="Repartición GDE" name="reparticion_gde.nombre" onChange={handleSelectChange} value={formData.reparticion_gde?.nombre || ""}>
              <option value="" disabled>Seleccionar repartición</option>
              {reparticionesGde.sort((a, b) => a.nombre.localeCompare(b.nombre)).map((reparticion) => (
                <option key={reparticion.id} value={reparticion.nombre}>{reparticion.nombre}</option>
              ))}
            </Form.Select>
          </FloatingLabel>
          {errors.reparticion_id && <div className={styles.errors}>{errors.reparticion_id}</div>}

          <FloatingLabel controlId="floatingInputNombre" label="Nombre" className="mb-3">
            <Form.Control type="text" placeholder="Nombre" name="nombre" onChange={handleInputChange} value={formData.nombre || ""} />
          </FloatingLabel>
          {errors.nombre && <div className={styles.errors}>{errors.nombre}</div>}

          </Modal.Body>
          <Modal.Footer className={styles.modalFooter}>
          <Button variant="primary" type="submit">Aceptar</Button>
          </Modal.Footer>
      </Form>
    </Modal>
  );
}
